import React from 'react';
import { usePaywallStore } from '@/stores/paywallStore';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';
import type { PaywallVariant } from '@/stores/paywallStore';

/**
 * Canvas paywall overlay — when credits are exhausted or trial needed:
 *
 * Shows a persistent bottom-left card as a gentle reminder.
 * Click → opens PaywallModal or ExhaustedTrialModal. Generation actions also trigger the paywall.
 *
 * DOES NOT block: panning, zooming, viewing images, downloading, sticky notes.
 *
 * Handles all 5 scenarios per the Touchpoint Matrix.
 */
export function CanvasPaywallOverlay() {
  const { needsUpgrade, needsTrial, isCancelledTrial, isExhaustedTrial, isExhaustedPaid, isExhausted, currentPeriodEnd } = usePaywallCheck();
  const isPaywalled = needsUpgrade || needsTrial || isExhausted;
  const isModalOpen = usePaywallStore((s) => s.isModalOpen);
  const openPaywall = usePaywallStore((s) => s.openPaywall);

  // Only show the persistent card when paywalled AND modal is closed
  if (!isPaywalled || isModalOpen) return null;

  let formattedDate = 'soon';
  if (currentPeriodEnd) {
    const endDate = new Date(currentPeriodEnd);
    const diffDays = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const dateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    formattedDate = diffDays <= 0 ? 'today' : diffDays === 1 ? 'tomorrow' : diffDays <= 14 ? `in ${diffDays} days` : dateStr;
  }

  // Determine scenario config
  let variant: PaywallVariant;
  let title: string;
  let subtitle: React.ReactNode;
  let iconType: 'play' | 'lock' | 'hourglass';

  if (needsTrial) {
    // Scenario A
    variant = 'trial';
    title = 'Start your free trial';
    subtitle = <span>7-day trial · <span style={{ color: 'rgba(200,212,90,0.8)' }}>No charge</span></span>;
    iconType = 'play';
  } else if (isCancelledTrial) {
    // Scenario B
    variant = 'cancelled_trial';
    title = 'Trial cancelled';
    subtitle = <span>Subscribe from <span style={{ color: 'rgba(200,212,90,0.8)' }}>$19/mo</span></span>;
    iconType = 'lock';
  } else if (isExhaustedTrial) {
    // Scenario C
    variant = 'exhausted_trial';
    title = 'Credits used up';
    subtitle = <span>Plan starts {formattedDate}</span>;
    iconType = 'hourglass';
  } else if (isExhaustedPaid) {
    // Scenario D
    variant = 'exhausted';
    title = 'Out of credits';
    subtitle = <span>Credits refresh {formattedDate}</span>;
    iconType = 'lock';
  } else {
    // Scenario E
    variant = 'upgrade';
    title = 'Upgrade to create';
    subtitle = <span>From <span style={{ color: 'rgba(200,212,90,0.8)' }}>$19/mo</span></span>;
    iconType = 'lock';
  }

  const isTrialStyle = iconType === 'play';
  const isWarningStyle = iconType === 'hourglass';

  return <PersistentCard
    onUpgrade={() => openPaywall('canvas', variant)}
    title={title}
    subtitle={subtitle}
    iconType={iconType}
    isTrialStyle={isTrialStyle}
    isWarningStyle={isWarningStyle}
  />;
}

/* ─── Persistent Bottom-Left Card ──────────────────── */
function PersistentCard({ onUpgrade, title, subtitle, iconType, isTrialStyle, isWarningStyle }: {
  onUpgrade: () => void;
  title: string;
  subtitle: React.ReactNode;
  iconType: 'play' | 'lock' | 'hourglass';
  isTrialStyle: boolean;
  isWarningStyle: boolean;
}) {
  const iconBg = isTrialStyle
    ? 'linear-gradient(135deg, rgba(200,212,90,0.15) 0%, rgba(168,180,74,0.15) 100%)'
    : isWarningStyle
      ? 'linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(245,158,11,0.15) 100%)'
      : 'linear-gradient(135deg, rgba(251,146,60,0.15) 0%, rgba(239,68,68,0.15) 100%)';
  const iconBorder = isTrialStyle
    ? '1px solid rgba(200,212,90,0.25)'
    : isWarningStyle
      ? '1px solid rgba(251,191,36,0.25)'
      : '1px solid rgba(251,146,60,0.2)';
  const iconColor = isTrialStyle ? '#c8d45a' : isWarningStyle ? '#fbbf24' : '#fb923c';

  return (
    <div
      className="absolute bottom-4 left-4 z-[100] w-[280px]"
      style={{ animation: 'persistentSlideIn 0.3s ease-out both' }}
    >
      <button
        onClick={onUpgrade}
        className="w-full text-left rounded-xl overflow-hidden group transition-all hover:scale-[1.02] active:scale-[0.98]"
        style={{
          background: 'linear-gradient(135deg, rgba(29,41,57,0.95) 0%, rgba(21,28,40,0.98) 100%)',
          border: isTrialStyle
            ? '1px solid rgba(200,212,90,0.2)'
            : isWarningStyle
              ? '1px solid rgba(251,191,36,0.2)'
              : '1px solid rgba(200,212,90,0.15)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="p-4 flex items-center gap-3">
          {/* Icon */}
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: iconBg, border: iconBorder }}
          >
            {iconType === 'play' ? (
              <svg className="w-5 h-5" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : iconType === 'hourglass' ? (
              <svg className="w-5 h-5" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white group-hover:text-accent-300 transition-colors">
              {title}
            </p>
            <p className="text-xs text-white/40">
              {subtitle}
            </p>
          </div>
          <svg className="w-4 h-4 text-white/20 group-hover:text-accent-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Subtle gradient accent line at bottom */}
        <div className="h-0.5 bg-gradient-to-r from-transparent via-accent-400/30 to-transparent" />
      </button>

      <style>{`
        @keyframes persistentSlideIn {
          from { opacity: 0; transform: translateY(16px) translateX(-8px); }
          to { opacity: 1; transform: translateY(0) translateX(0); }
        }
      `}</style>
    </div>
  );
}
